import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";
import { t } from "../i18n";
import { lineReveal } from "../motion";

export default function Hero() {
  const ref = useRef(null);
  const h = t.de.hero;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen flex items-center overflow-hidden bg-navy-900">
      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/11861957/pexels-photo-11861957.jpeg"
          alt="Moderne Glasarchitektur"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/85 to-navy-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-transparent to-navy-900/30" />
      </motion.div>

      <motion.div style={{ opacity }} className="container mx-auto px-6 md:px-12 max-w-7xl relative pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-gold-500" />
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-400" data-testid="hero-overline">
            {h.overline}
          </span>
        </motion.div>

        <h1 className="text-white font-light tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl" data-testid="hero-headline">
          {h.headline.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="inline-block"
              >
                {i === 2 ? <span className="gold-gradient-text font-normal">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed"
          data-testid="hero-sub"
        >
          {h.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-11 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/kontakt"
            data-testid="hero-cta-quote"
            className="group inline-flex items-center justify-center gap-2 bg-gold-500 text-white rounded-full px-8 py-4 font-medium transition-all hover:bg-gold-600 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(197,160,89,0.35)]"
          >
            {h.cta1}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <button
            onClick={() => scrollTo("services")}
            data-testid="hero-cta-contact"
            className="inline-flex items-center justify-center gap-2 backdrop-blur-md bg-white/10 border border-white/20 text-white rounded-full px-8 py-4 font-medium transition-all hover:bg-white/20 hover:-translate-y-1"
          >
            {h.cta2}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        style={{ opacity }}
        onClick={() => scrollTo("about")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 flex flex-col items-center gap-2"
        aria-label={h.scroll}
      >
        <span className="text-[10px] uppercase tracking-[0.25em]">{h.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
}
